import { FC, useCallback, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { DiamGroup, EmailType } from 'models/customer';
import { useTranslations } from 'hooks/useTranslations';
import InputField from 'components/UI/InputField';
import Button from 'components/UI/Buttons/Button';
import Loader from 'components/UI/Loader';
import Modal from 'components/UI/Modal';

import { ModalForm, ModalTitle } from 'styles';
import { ButtonGroup, Text, Services, Group, Info } from './styled';

type ValidateChangeEmail = {
  code: string;
  password: string;
};

// Zod schema
const schema = z.object({
  password: z.string().min(1, 'input.password_required')
});

type Props = {
  email: string | null;
  emailType: EmailType | null;
  isOpen: boolean;
  defaultCode?: string | null;
  isLoading: boolean;
  groups: DiamGroup[];
  submit: (values: ValidateChangeEmail) => void;
  cancel?: () => void;
};

const ConfirmChangeEmailModal: FC<Props> = ({
  email,
  emailType = 'eContact',
  isOpen,
  defaultCode,
  isLoading,
  groups,
  submit,
  cancel
}) => {
  const t = useTranslations();

  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField
  } = useForm({
    resolver: zodResolver(schema)
  });

  const isPrimaryContact = emailType === 'primaryContact';

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    ({ password }) => {
      if (defaultCode) {
        submit({ code: defaultCode, password });
      }
    },
    [submit, defaultCode]
  );

  // Render services
  const services = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!groups || !groups.length) {
      return null;
    }
    return (
      <div>
        <Text>{t('change_email.confirm.text_services')}</Text>
        <ul>
          {groups.map((item) => (
            <Group key={item.groupId}>{item.name}</Group>
          ))}
        </ul>
      </div>
    );
  }, [groups, isLoading, t]);

  // Render info box
  const infoBox = useMemo(() => {
    if (!email) {
      return null;
    }
    return (
      <Info>
        <Text>
          {t(
            isPrimaryContact
              ? 'change_email.confirm.instructions_primary_contact'
              : 'change_email.confirm.instructions',
            { email }
          )}
        </Text>
      </Info>
    );
  }, [isPrimaryContact, email, t]);

  return (
    <Modal isOpen={isOpen}>
      <ModalTitle>
        {t(
          isPrimaryContact
            ? 'change_email.confirm.title_primary_contact'
            : 'change_email.confirm.title'
        )}
      </ModalTitle>
      <ModalForm onSubmit={handleSubmit(onSubmit)}>
        <Services>
          <Text>
            {t(
              isPrimaryContact
                ? 'change_email.confirm.description_primary_contact'
                : 'change_email.confirm.description'
            )}
          </Text>
          {services}
        </Services>
        {infoBox}
        <InputField
          name="password"
          description="input.password_label"
          type="password"
          error={errors.password}
          register={register('password')}
          reset={resetField}
          outlined
        />
        <ButtonGroup>
          <Button color="transparent" textColor="primary" onClick={cancel}>
            {t('change_email.confirm.btn_cancel')}
          </Button>
          <Button submit>{t('change_email.confirm.btn_confirm')}</Button>
        </ButtonGroup>
      </ModalForm>
    </Modal>
  );
};

export default ConfirmChangeEmailModal;
